import { useMemo } from 'react';
import { compact } from 'lodash';

import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';
import { PROJECT_TYPE, type ProjectType } from 'src/data/project';
import {
  type MultiPromptTemplate,
  type RecentlyUsedTemplate,
  type SubTemplate,
  TEMPLATE_TYPE,
} from 'src/data/template';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { TemplatesStore } from 'src/store/templates';

export interface RecentlyUsedTemplateCardData {
  id: string;
  projectType: ProjectType;
  generationTool: GenerationTool;
  customToolData?: MultiPromptTemplate;
  subTemplateData?: SubTemplate;
}

const useTemplatesState = createStateSelectorHook(TemplatesStore, ['recentlyUsedTemplates']);

/**
 * Runs made with tools the gallery no longer offers. The list comes from the backend, so these keep
 * arriving, and a card is the one place left that would start a fresh generation with one.
 *
 * The long-form pair predate blank and prompt long-form projects being retired: a card for one can
 * only offer a project that can no longer be created, and retyping it to a document project would
 * build one around a tool that has no short-form form — failing at generation rather than at the
 * click. The three below it are campaigns and a landing page that released templates cover now, and
 * those templates have cards of their own.
 */
const RETIRED_GENERATION_TOOLS: GenerationTool[] = [
  GENERATION_TOOL.longFormBlank,
  GENERATION_TOOL.longFormGeneral,
  GENERATION_TOOL.productLaunchMultiPrompt,
  GENERATION_TOOL.landingPageProductAnnouncementMultiPrompt,
  GENERATION_TOOL.emailDripMultiPrompt,
];

export const useRecentlyUsedTemplatesCards = () => {
  const { recentlyUsedTemplates } = useTemplatesState();

  return useMemo(() => {
    if (!recentlyUsedTemplates) {
      return [];
    }

    return compact(recentlyUsedTemplates.map(mapCard)).filter(
      (card) => !RETIRED_GENERATION_TOOLS.includes(card.generationTool)
    );
  }, [recentlyUsedTemplates]);
};

const mapCard = (
  recentlyUsedTemplate: RecentlyUsedTemplate
): RecentlyUsedTemplateCardData | undefined => {
  const { type, generationTool } = recentlyUsedTemplate;

  switch (type) {
    case TEMPLATE_TYPE.customMultiPrompt: {
      const customTool = recentlyUsedTemplate.customMultiPromptTemplate;
      if (!customTool) {
        return;
      }

      return {
        id: `${generationTool}-customTool-${customTool.id}`,
        projectType: PROJECT_TYPE.document,
        generationTool: generationTool || GENERATION_TOOL.customMultiPrompt,
        customToolData: customTool,
      };
    }

    case TEMPLATE_TYPE.subTemplate: {
      const subTemplate = recentlyUsedTemplate.subTemplate;
      if (!subTemplate) {
        return;
      }

      return {
        id: `${generationTool}-subTemplate-${subTemplate.id}`,
        projectType: PROJECT_TYPE.document,
        generationTool,
        subTemplateData: subTemplate,
      };
    }

    case TEMPLATE_TYPE.generationTool: {
      return {
        id: `${generationTool}`,
        projectType: PROJECT_TYPE.document,
        generationTool,
      };
    }
  }
};
